import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { UserManagementService } from "../../../Services/userManagment.service";
import { ValidationService } from "../../../Services/Validation/validation.service";
import { LoginService } from "../../../Services/login.service";
import { CatalogService } from "../../../Services/CatalogManagement/catalog.service";
import { SystemService } from "../../../Services/system.service";
import { FeatureSystem } from "../../../Model/Catalog/FeatureModels/FeatureSystem";
import { ModelService } from "../../../Services/model.service";
import { ValidFeatureService } from "../../../Services/validFeature.service";
import { Feature } from "../../../Model/Catalog/FeatureModels/Feature";
import { FeatureValue } from "../../../Model/classification/FeatureValue";
import { Catalog } from "../../../Model/Catalog/Catalog";
import { NpInputControlContext } from "../../../ui/inputControl/np-input-control-context";
import { fieldNames } from "../../../Model/FieldNames";

@Component({
    selector: 'np-product-texts',
    standalone: false,
    templateUrl: './texts.component.html',
    styleUrl: './texts.component.css'
})
export class TextsComponent implements OnInit {
    context = inject(NpInputControlContext);
    @Input() textsTemplateEditor = false;

    private _marketingClaim: string = "";

    showHTMLEditor: boolean = true;
    benefitFeatures: Feature[] = [];
    topFeatures: Feature[] = [];
    selectedBenefitFeature: Feature;
    selectedTopFeature: Feature;
    wawiFieldsForTextsAvailable = false;
    benefitsInvalid = false;
    benefitsValidationMessage = "";
    topFeaturesInvalid = false;
    topFeaturesValidationMessage = "";

    constructor(
        public modelService: ModelService,
        public translate: TranslateService,
        public userManagement: UserManagementService,
        public validationService: ValidationService,
        public loginService: LoginService,
        public catalogService: CatalogService,
        public systemService: SystemService,
        public validFeatureService: ValidFeatureService,
    ) {
        this.createNewBenefitFeature = this.createNewBenefitFeature.bind(this);
        this.createNewTopFeature = this.createNewTopFeature.bind(this);
    }

    ngOnInit() {
        this.initMarketingClaim();
        let wawiCustomFields = this.loginService.wawiSettings?.customFields?.filter(x => x.tabOption === "Texte" && !x.customTabId);
        let wawiLinesForTexts =
            this.loginService.wawiSettings?.line1TabOption === "Texte" ||
            this.loginService.wawiSettings?.line2TabOption === "Texte" ||
            this.loginService.wawiSettings?.line3TabOption === "Texte" ||
            this.loginService.wawiSettings?.line4TabOption === "Texte";
        this.wawiFieldsForTextsAvailable = (wawiCustomFields?.length > 0) || wawiLinesForTexts;
        this.initFeatures('udf_NMTOPFEATURES-1.0');
        this.initFeatures('udf_NMBENEFITS-1.0');

        if (this.loginService.currentCustomer?.automaticValidation) {
            this.validateBenefits();
            this.validateTopFeatures();
        }
    }

    async validateBenefits() {
        if (this.context.templateView || this.textsTemplateEditor) {
            return;
        }
        let result = await this.validationService.validateFieldDirect(fieldNames.benefits);
        this.benefitsInvalid = !result.isValid && !result.noValidation;
        this.benefitsValidationMessage = this.benefitsInvalid ? result.message : "";
    }

    async validateTopFeatures() {
        if (this.context.templateView || this.textsTemplateEditor) {
            return;
        }
        let result = await this.validationService.validateFieldDirect(fieldNames.topFeatures);
        this.topFeaturesInvalid = !result.isValid && !result.noValidation;
        this.topFeaturesValidationMessage = this.topFeaturesInvalid ? result.message : "";
    }

    private async validateFeatureGroup(featureSystemName: string) {
        if (featureSystemName === 'udf_NMBENEFITS-1.0') {
            await this.validateBenefits();
        } else if (featureSystemName === 'udf_NMTOPFEATURES-1.0') {
            await this.validateTopFeatures();
        }
    }

    private initMarketingClaim() {
        let system = this.context.model?.featureSystems?.find(x => x.referenceFeatureSystemName === 'udf_NMMARKETINGCLAIM-1.0');
        if (system && system.features?.length > 0 && system.features[0].featureValues?.length > 0) {
            this._marketingClaim = system.features[0].featureValues[0].value ?? "";
        }
    }

    get marketingClaim(): string {
        return this._marketingClaim;
    }

    set marketingClaim(value: string) {
        this._marketingClaim = value;
        this.applyMarketingClaimToModel(value ?? "");
    }

    private applyMarketingClaimToModel(value: string) {
        if (!this.context.model?.featureSystems) {
            return;
        }

        let system = this.context.model.featureSystems.find(x => x.referenceFeatureSystemName === 'udf_NMMARKETINGCLAIM-1.0');
        if (!system) {
            system = new FeatureSystem();
            system.referenceFeatureSystemName = 'udf_NMMARKETINGCLAIM-1.0';
            system.order =
                (this.context.model.featureSystems.reduce((maxOrder, fs) => Math.max(maxOrder, fs.order ?? 0), 0) ?? 0) + 1;
            this.context.model.featureSystems.push(system);
        }

        let feature: Feature;
        if (system.features.length == 0) {
            feature = new Feature();
            feature.forder = 1;
            system.features.push(feature);
        } else {
            feature = system.features[0];
        }

        feature.value = value;
        feature.name = value;

        if (!feature.featureValues || feature.featureValues.length == 0) {
            let featureValue = new FeatureValue();
            featureValue.order = 1;
            feature.featureValues = [featureValue];
        }
        feature.featureValues[0].value = value;
    }

    private initFeatures(featureSystemName: string) {
        let system = this.context.model?.featureSystems?.find(x => x.referenceFeatureSystemName === featureSystemName);
        if (system && system.features?.length > 0) {
            if (featureSystemName === 'udf_NMBENEFITS-1.0') {
                this.benefitFeatures = system.features;
            } else if (featureSystemName === 'udf_NMTOPFEATURES-1.0') {
                this.topFeatures = system.features;
            }
        }
    }

    public async createNewBenefitFeature(event?: any) {
        await this.createNewFeature('udf_NMBENEFITS-1.0', event);
    }

    public async createNewTopFeature(event?: any) {
        await this.createNewFeature('udf_NMTOPFEATURES-1.0', event);
    }

    public async updateBenefitFeatureValue(event: any, feature: Feature) {
        await this.updateFeatureValue(event, feature, 'udf_NMBENEFITS-1.0');
    }

    public async updateTopFeatureValue(event: any, feature: Feature) {
        await this.updateFeatureValue(event, feature, 'udf_NMTOPFEATURES-1.0');
    }

    private getFeatureList(featureSystemName: string): Feature[] {
        if (featureSystemName === 'udf_NMBENEFITS-1.0') {
            return this.benefitFeatures;
        }

        if (featureSystemName === 'udf_NMTOPFEATURES-1.0') {
            return this.topFeatures;
        }

        return [];
    }

    private setFeatureList(featureSystemName: string, features: Feature[]): void {
        if (featureSystemName === 'udf_NMBENEFITS-1.0') {
            this.benefitFeatures = features;
            return;
        }

        if (featureSystemName === 'udf_NMTOPFEATURES-1.0') {
            this.topFeatures = features;
        }
    }

    public async createNewFeature(featureSystemName: string, event?: any) {
        const existingFeatureSystem = this.context.model.featureSystems.find(
            x => x.referenceFeatureSystemName === featureSystemName
        );

        if (!existingFeatureSystem) {
            const newFeatureSystem = await this.createFeatureSystem(featureSystemName);
            this.context.model.featureSystems.push(newFeatureSystem);
            this.setFeatureList(featureSystemName, newFeatureSystem.features);
        } else {
            const currentFeatures = existingFeatureSystem.features ?? [];
            const newFeature = new Feature();

            newFeature.forder =
                (currentFeatures.reduce((maxOrder, f) => Math.max(maxOrder, f.forder ?? 0), 0) ?? 0) + 1;
            newFeature.value = "";
            newFeature.name = "";

            existingFeatureSystem.features.push(newFeature);
            this.setFeatureList(featureSystemName, existingFeatureSystem.features);
        }

        await this.update(event);

        await this.validateFeatureGroup(featureSystemName);
    }

    public async updateFeatureValue(event: any, feature: Feature, featureSystemName: string) {
        const features = this.getFeatureList(featureSystemName);
        const updatedFeature = features.find(f => f.forder === feature.forder);

        if (updatedFeature) {
            updatedFeature.value = event.value;
            updatedFeature.name = event.value;

            const featureValue = new FeatureValue();
            featureValue.value = event.value;
            featureValue.order = 1;
            updatedFeature.featureValues = [featureValue];
        }

        const field = updatedFeature
            ? this.getFeatureFieldName(featureSystemName, features.indexOf(updatedFeature) + 1)
            : undefined;
        await this.update(event, field);

        await this.validateFeatureGroup(featureSystemName);
    }

    private getFeatureFieldName(featureSystemName: string, position: number): string {
        const prefix = featureSystemName === 'udf_NMBENEFITS-1.0' ? fieldNames.benefitPrefix : fieldNames.topFeaturePrefix;
        return prefix + position;
    }

    get hasTranslation(): Boolean {
        if (this.context.productTranslation == null) {
            return false;
        }
        return true;
    }


    showHTMLEditorChanged() {
        this.catalogService
            .getProduct(this.context.model.id, this.context.model.customerId, this.catalogService.catalog.id)
            .subscribe(() => {
                this.context.model = this.catalogService.product;
            });
    }

    public translationUpdate(txtOrg, txtTran) {
        this.context.translationUpdate.emit({ txtOrg: txtOrg, txtTran: txtTran });
    }

    public elementSelectFunction(event) {
        this.context.elementSelected.emit(event);
    }

    public excelElementDrop(e) {
        this.context.elementDropped.emit(e);
    }

    async updateMarketingClaim(event) {
        let system = this.context.model.featureSystems.find(x => x.referenceFeatureSystemName === 'udf_NMMARKETINGCLAIM-1.0');
        if (system && !system.validFeatureSystemId) {
            system.validFeatureSystemId = await this.validFeatureService.getValidIdForSystemName(
                'udf_NMMARKETINGCLAIM-1.0',
                this.loginService.currentCustomer.id
            );
        }
        await this.update(event, fieldNames.marketingClaim);
    }

    private async createFeatureSystem(featureSystemName: string): Promise<FeatureSystem> {

        let validFeatureSystemId = await this.validFeatureService.getValidIdForSystemName(
            featureSystemName,
            this.loginService.currentCustomer.id
        );

        let newFeatureSystem = new FeatureSystem();
        newFeatureSystem.referenceFeatureSystemName = featureSystemName;
        newFeatureSystem.validFeatureSystemId = validFeatureSystemId;
        newFeatureSystem.order =
            (this.context.model.featureSystems.reduce((maxOrder, fs) => Math.max(maxOrder, fs.order ?? 0), 0) ?? 0) + 1;

        let feature = new Feature();
        feature.value = "";
        feature.name = "";
        feature.forder = 1;
        newFeatureSystem.features.push(feature);
        return newFeatureSystem;

    }

    async update(event = null, field?: string, system: string = '-1', element: string = '-1') {
        if (!event.event && !event.toggleLinkedFields && !event.dField)
            return;

        if (event) {
            event.dField = field;
            event.dSystem = system;
            event.dElement = element;
            event.createTemplateItem = true;
        }

        this.context.valueChanged.emit(event);
    }

    async onFeaturesReorder(event: any, featureSystemName: string) {
        const fromIndex = event.fromIndex;
        const toIndex = event.toIndex;

        if (fromIndex === toIndex) {
            return;
        }

        const features = [...this.getFeatureList(featureSystemName)];
        const [moved] = features.splice(fromIndex, 1);
        features.splice(toIndex, 0, moved);

        features.forEach((feature, index) => {
            feature.forder = index + 1;
        });

        this.setFeatureList(featureSystemName, features);

        const featureSystem = this.context.model.featureSystems.find(
            x => x.referenceFeatureSystemName === featureSystemName
        );

        if (featureSystem) {
            featureSystem.features = features;
        }

        await this.update(event);

        await this.validateFeatureGroup(featureSystemName);
    }

    onBenefitFeatureRowClick(benefitFeature: Feature): void {
        this.selectedBenefitFeature = benefitFeature;
    }

    onTopFeatureRowClick(topFeature: Feature): void {
        this.selectedTopFeature = topFeature;
    }

    async onFeatureDeleteClick(feature: Feature, event: any, featureSystemName: string) {
        const featureSystem = this.context.model.featureSystems.find(
            x => x.referenceFeatureSystemName === featureSystemName
        );

        let updatedFeatures: Feature[];

        if (featureSystem?.features) {
            updatedFeatures = featureSystem.features.filter(f => f.forder !== feature.forder);
            featureSystem.features = updatedFeatures;
        } else {
            updatedFeatures = this.getFeatureList(featureSystemName).filter(f => f.forder !== feature.forder);
        }

        updatedFeatures.forEach((item, index) => {
            item.forder = index + 1;
        });

        this.setFeatureList(featureSystemName, updatedFeatures);

        if (featureSystemName === 'udf_NMBENEFITS-1.0' && this.selectedBenefitFeature?.forder === feature.forder) {
            this.selectedBenefitFeature = null;
        }

        if (featureSystemName === 'udf_NMTOPFEATURES-1.0' && this.selectedTopFeature?.forder === feature.forder) {
            this.selectedTopFeature = null;
        }

        await this.update(event);

        await this.validateFeatureGroup(featureSystemName);
    }

    public showClearButton(field: string): boolean {
        return !this.readOnlyForLinkedFields(field) && !this.context.templateView;
    }

    public readOnlyForLinkedFields(field: string, system: string = '-1', element: string = '-1'): boolean {

        if (!this.userManagement.canView(field)) {
            return true;
        }
        if (field != null) {
            var isLinked = this.context.model.isFieldLinked(field, system, element);
            if (isLinked && this.context.model.isChild != null && this.context.model.isChild) {
                return true;
            }
        }
        return false;
    }

    isVirtualCatalog(): boolean {
        return this.catalogService?.catalog?.isVirtual;
    }

    readOnly(name: string, catalog: Catalog = this.catalogService.catalog): boolean {
        return this.isVirtualCatalog() || !this.userManagement.canEdit(name, catalog);
    }

    get showTextsValidationSummary(): boolean {
        return this.loginService.currentCustomer.automaticValidation
            && this.validationService.validationResult.textsMessages?.length > 0;
    }

    get textsValidationMessages(): string[] {
        return this.validationService.validationResult.textsMessages ?? [];
    }

    translateMessage(msg: string): string {
        return this.translate.instant(msg);
    }

}
